/**
 * Detalle de orden — réplica visual del backoffice de Skeleton:
 *   - Header con número orden + status + fecha + acciones
 *   - Stepper de progreso (Pendiente → En Proceso → Enviado → Entregado)
 *   - Items de la orden con totales
 *   - Sidebar derecho: Cliente, Dirección, Pago, Email confirmación, Transacciones, Tracking, Notas
 */
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, Badge, Button, Input, Textarea } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import {
  ArrowLeft, Clock, Box, Truck, CheckCircle2, User, MapPin, CreditCard,
  Mail, Send, RefreshCw, Package, FileText, ChevronRight, XCircle, Loader2,
} from 'lucide-react'

type OrderStatus = 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded'

export const Route = createFileRoute('/store/$id/orders/$orderId')({
  component: OrderDetailPage,
})

interface OrderDetail {
  order: {
    id: string
    orderNumber: string
    customerId: string | null
    guestEmail: string | null
    guestName: string | null
    status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
    paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded'
    currency: string
    subtotal: number
    taxAmount: number
    shippingAmount: number
    discountAmount: number
    total: number
    shippingAddress: Record<string, any>
    billingAddress: Record<string, any>
    paymentMethod: string
    confirmationEmailSentAt: string | null
    shippedAt: string | null
    trackingNumber: string | null
    trackingUrl: string | null
    notes: string | null
    createdAt: string
  }
  items: Array<{
    id: string
    productId: string | null
    variantId: string | null
    quantity: number
    unitPrice: number
    productSnapshot: any
  }>
  customer: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
    phone: string | null
  } | null
  transactions: Array<{
    id: string
    amount: number
    currency: string
    status: 'pending' | 'completed' | 'failed' | 'refunded'
    providerTransactionId: string | null
    metadata: Record<string, unknown>
    createdAt: string
  }>
}

function OrderDetailPage() {
  const { id, orderId } = Route.useParams()
  const { activeTenantId } = useAuth()
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['order', id, orderId],
    queryFn: () => tenantApi.get<{ data: OrderDetail }>(`/api/v1/orders/${orderId}`),
    enabled: !!activeTenantId,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['order', id, orderId] })

  const updateStatus = useMutation({
    mutationFn: (status: OrderStatus) =>
      tenantApi.patch(`/api/v1/orders/${orderId}/status`, { status }),
    onSuccess: invalidate,
  })

  const updatePayment = useMutation({
    mutationFn: (paymentStatus: PaymentStatus) =>
      tenantApi.patch(`/api/v1/orders/${orderId}/payment-status`, { paymentStatus }),
    onSuccess: invalidate,
  })

  const updateTracking = useMutation({
    mutationFn: (input: { trackingNumber: string | null; trackingUrl: string | null }) =>
      tenantApi.patch(`/api/v1/orders/${orderId}/tracking`, input),
    onSuccess: invalidate,
  })

  const updateNotes = useMutation({
    mutationFn: (notes: string | null) =>
      tenantApi.patch(`/api/v1/orders/${orderId}/notes`, { notes }),
    onSuccess: invalidate,
  })

  const cancelOrder = useMutation({
    mutationFn: (reason?: string) =>
      tenantApi.post(`/api/v1/orders/${orderId}/cancel`, { reason }),
    onSuccess: invalidate,
  })

  if (isLoading) return <div className="p-8 text-sm text-neutral-500">Cargando orden...</div>
  if (error) return <div className="p-8 text-sm text-red-400">Error: {(error as Error).message}</div>
  if (!data?.data) return <div className="p-8 text-sm text-neutral-500">Orden no encontrada</div>

  const { order, items, customer, transactions } = data.data
  const dateStr = new Date(order.createdAt).toLocaleString('es-CO', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Link
            to="/store/$id/orders"
            params={{ id }}
            className="p-1.5 mt-1 rounded-md hover:bg-neutral-800 text-neutral-400 hover:text-white transition"
          >
            <ArrowLeft className="size-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight font-mono">Orden {order.orderNumber}</h1>
              <StatusBadge status={order.status} />
            </div>
            <p className="text-sm text-neutral-500 mt-1 capitalize">{dateStr}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'cancelled' && (
            <Button
              variant="outline"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => updateStatus.mutate('shipped')}
            >
              {updateStatus.isPending ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Truck className="size-4 mr-2" />}
              Marcar como enviado
            </Button>
          )}
          <select
            value={order.status}
            disabled={updateStatus.isPending || order.status === 'cancelled'}
            onChange={(e) => updateStatus.mutate(e.target.value as OrderStatus)}
            className="h-8 px-2 bg-neutral-900 border border-neutral-700 rounded-md text-sm"
          >
            <option value="pending">Pendiente</option>
            <option value="processing">En proceso</option>
            <option value="shipped">Enviada</option>
            <option value="delivered">Entregada</option>
            <option value="cancelled" disabled>Cancelada</option>
          </select>
          {order.status !== 'cancelled' && order.status !== 'delivered' && (
            <Button
              variant="outline"
              size="sm"
              disabled={cancelOrder.isPending}
              onClick={() => {
                const reason = window.prompt('Motivo de la cancelación (opcional):') ?? undefined
                if (reason !== null) cancelOrder.mutate(reason || undefined)
              }}
            >
              {cancelOrder.isPending ? <Loader2 className="size-4 mr-2 animate-spin" /> : <XCircle className="size-4 mr-2" />}
              Cancelar
            </Button>
          )}
        </div>
      </div>

      {/* Progreso stepper */}
      <Card className="p-5 mb-4">
        <p className="text-sm font-semibold mb-4">Progreso</p>
        <ProgressStepper status={order.status} />
      </Card>

      {/* Layout: items izquierda + sidebar derecha */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* Items + totales */}
        <div className="space-y-4">
          <Card className="p-5">
            <p className="text-sm font-semibold mb-3">Items ({items.length})</p>
            <div className="space-y-3">
              {items.map((item) => {
                const snap = item.productSnapshot as any
                const variant = snap?.attributes
                  ? Object.entries(snap.attributes).map(([, v]) => v as string).join(' / ')
                  : null
                const image = snap?.images?.[0]
                return (
                  <div key={item.id} className="flex items-center gap-3">
                    <div className="size-12 rounded-md border border-neutral-800 bg-neutral-950 overflow-hidden flex-shrink-0">
                      {image ? (
                        <img src={image} alt={snap.name} className="size-full object-cover" />
                      ) : (
                        <div className="size-full flex items-center justify-center text-neutral-700">
                          <Package className="size-5" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium uppercase tracking-wide">{snap?.name ?? 'Producto'}</p>
                      {variant && <p className="text-xs text-neutral-500 mt-0.5">{`Talla ${variant}`}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-mono text-sm font-bold">{formatMoney(item.unitPrice, order.currency)}</p>
                      <p className="text-xs text-neutral-500 mt-0.5">x{item.quantity}</p>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Totales */}
            <div className="border-t border-neutral-800 mt-5 pt-4 space-y-1.5">
              <Total label="Subtotal" value={formatMoney(order.subtotal, order.currency)} />
              <Total label="Impuestos" value={formatMoney(order.taxAmount, order.currency)} />
              <Total label="Envío" value={formatMoney(order.shippingAmount, order.currency)} />
              {order.discountAmount > 0 && (
                <Total label="Descuento" value={`-${formatMoney(order.discountAmount, order.currency)}`} />
              )}
              <div className="flex items-center justify-between pt-2 mt-2 border-t border-neutral-800">
                <span className="text-base font-bold">Total</span>
                <span className="font-mono text-base font-bold">{formatMoney(order.total, order.currency)}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar derecho */}
        <div className="space-y-3">
          {/* Cliente */}
          <Card className="p-4">
            <SidebarTitle icon={User}>Cliente</SidebarTitle>
            <p className="text-sm font-medium mt-1">
              {customer
                ? `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim() || customer.email
                : order.guestName ?? 'Invitado'}
            </p>
            <p className="text-xs text-neutral-500 mt-0.5">{customer?.email ?? order.guestEmail ?? '—'}</p>
            {(customer?.phone ?? order.shippingAddress.phone) && (
              <p className="text-xs text-neutral-500 mt-0.5 font-mono">{customer?.phone ?? order.shippingAddress.phone}</p>
            )}
            {!customer && (
              <Badge variant="muted" className="mt-2">Invitado</Badge>
            )}
          </Card>

          {/* Dirección de envío */}
          <Card className="p-4">
            <SidebarTitle icon={MapPin}>Dirección de Envío</SidebarTitle>
            <div className="text-sm text-neutral-300 mt-2 space-y-0.5">
              <p>{order.shippingAddress.address}</p>
              <p className="text-neutral-400">
                {[order.shippingAddress.city, order.shippingAddress.state].filter(Boolean).join(', ')}
              </p>
              <p className="text-neutral-400">
                {[order.shippingAddress.zipCode, order.shippingAddress.country].filter(Boolean).join(', ')}
              </p>
              {order.shippingAddress.phone && (
                <p className="text-neutral-500 font-mono pt-1">{order.shippingAddress.phone}</p>
              )}
            </div>
          </Card>

          {/* Pago */}
          <Card className="p-4">
            <SidebarTitle icon={CreditCard}>Pago</SidebarTitle>
            <div className="flex items-center justify-between mt-2 text-sm">
              <span className="text-neutral-400">Estado</span>
              <select
                value={order.paymentStatus}
                disabled={updatePayment.isPending}
                onChange={(e) => updatePayment.mutate(e.target.value as PaymentStatus)}
                className="h-7 px-2 bg-neutral-900 border border-neutral-700 rounded text-xs font-mono"
              >
                <option value="pending">pending</option>
                <option value="paid">paid</option>
                <option value="failed">failed</option>
                <option value="refunded">refunded</option>
              </select>
            </div>
            {updatePayment.error && (
              <p className="text-xs text-red-400 mt-2">{(updatePayment.error as Error).message}</p>
            )}
          </Card>

          {/* Email confirmación */}
          <Card className="p-4">
            <SidebarTitle icon={Mail}>Email de confirmación</SidebarTitle>
            {order.confirmationEmailSentAt ? (
              <>
                <p className="flex items-center gap-1.5 text-xs text-emerald-400 mt-2">
                  <CheckCircle2 className="size-3.5" /> Enviado
                </p>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {new Date(order.confirmationEmailSentAt).toLocaleString('es-CO')}
                </p>
              </>
            ) : (
              <p className="text-xs text-neutral-500 mt-2">No enviado todavía</p>
            )}
            <Button variant="ghost" size="sm" className="w-full mt-3" disabled>
              <Send className="size-3.5 mr-2" /> Reenviar email
            </Button>
          </Card>

          {/* Transacciones */}
          {transactions.length > 0 && (
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <SidebarTitle icon={CreditCard}>Transacciones</SidebarTitle>
                <button className="text-xs text-neutral-400 hover:text-white flex items-center gap-1">
                  <RefreshCw className="size-3" /> Sincronizar
                </button>
              </div>
              <div className="space-y-2 mt-3">
                {transactions.map((tx) => (
                  <div
                    key={tx.id}
                    className={cn(
                      'rounded-md border p-3',
                      tx.status === 'completed' && 'bg-emerald-950/30 border-emerald-800',
                      tx.status === 'failed' && 'bg-red-950/30 border-red-800',
                      tx.status === 'pending' && 'bg-yellow-950/30 border-yellow-800',
                      tx.status === 'refunded' && 'bg-neutral-900 border-neutral-700',
                    )}
                  >
                    <div className="flex items-center justify-between text-xs">
                      <span className={cn(
                        'flex items-center gap-1 font-medium',
                        tx.status === 'completed' && 'text-emerald-400',
                        tx.status === 'failed' && 'text-red-400',
                        tx.status === 'pending' && 'text-yellow-400',
                        tx.status === 'refunded' && 'text-neutral-400',
                      )}>
                        <CheckCircle2 className="size-3" />
                        {tx.status === 'completed' ? 'Completada' : tx.status === 'failed' ? 'Fallida' : tx.status === 'pending' ? 'Pendiente' : 'Reembolsada'}
                      </span>
                      <span className="font-mono">{formatMoney(tx.amount, tx.currency)}</span>
                    </div>
                    {tx.providerTransactionId && (
                      <p className="text-xs text-neutral-500 font-mono mt-1">ID: {tx.providerTransactionId}</p>
                    )}
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {new Date(tx.createdAt).toLocaleString('es-CO')}
                      {(tx.metadata as any)?.provider && ` · ${(tx.metadata as any).provider}`}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <TrackingCard order={order} updateTracking={updateTracking} />
          <NotesCard order={order} updateNotes={updateNotes} />
        </div>
      </div>
    </div>
  )
}

function TrackingCard({
  order,
  updateTracking,
}: {
  order: OrderDetail['order']
  updateTracking: ReturnType<typeof useMutation<unknown, Error, { trackingNumber: string | null; trackingUrl: string | null }>>
}) {
  const [trackingNumber, setTrackingNumber] = useState(order.trackingNumber ?? '')
  const [trackingUrl, setTrackingUrl] = useState(order.trackingUrl ?? '')

  return (
    <Card className="p-4">
      <SidebarTitle icon={Truck}>Tracking</SidebarTitle>
      <div className="space-y-2 mt-3">
        <Input
          placeholder="Número de tracking"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
        />
        <Input
          placeholder="URL de seguimiento"
          value={trackingUrl}
          onChange={(e) => setTrackingUrl(e.target.value)}
        />
        <Button
          size="sm"
          className="w-full"
          disabled={updateTracking.isPending}
          onClick={() =>
            updateTracking.mutate({
              trackingNumber: trackingNumber.trim() || null,
              trackingUrl: trackingUrl.trim() || null,
            })
          }
        >
          {updateTracking.isPending ? <><Loader2 className="size-3.5 mr-2 animate-spin" /> Guardando...</> : 'Guardar tracking'}
        </Button>
        {updateTracking.error && (
          <p className="text-xs text-red-400">{(updateTracking.error as Error).message}</p>
        )}
      </div>
    </Card>
  )
}

function NotesCard({
  order,
  updateNotes,
}: {
  order: OrderDetail['order']
  updateNotes: ReturnType<typeof useMutation<unknown, Error, string | null>>
}) {
  const [notes, setNotes] = useState(order.notes ?? '')
  return (
    <Card className="p-4">
      <SidebarTitle icon={FileText}>Notas</SidebarTitle>
      <Textarea
        className="mt-2"
        rows={3}
        placeholder="Notas internas (ej: Orden de prueba, cliente VIP...)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <Button
        size="sm"
        className="w-full mt-2"
        disabled={updateNotes.isPending}
        onClick={() => updateNotes.mutate(notes.trim() || null)}
      >
        {updateNotes.isPending ? <><Loader2 className="size-3.5 mr-2 animate-spin" /> Guardando...</> : 'Guardar notas'}
      </Button>
      {updateNotes.error && (
        <p className="text-xs text-red-400 mt-2">{(updateNotes.error as Error).message}</p>
      )}
    </Card>
  )
}

function SidebarTitle({ icon: Icon, children }: { icon: React.ComponentType<{ className?: string }>; children: React.ReactNode }) {
  return (
    <p className="flex items-center gap-1.5 text-xs font-semibold text-neutral-300 uppercase tracking-wider">
      <Icon className="size-3.5" /> {children}
    </p>
  )
}

function StatusBadge({ status }: { status: OrderDetail['order']['status'] }) {
  const cfg = (() => {
    switch (status) {
      case 'pending': return { label: 'Pendiente', variant: 'warning' as const }
      case 'processing': return { label: 'En Proceso', variant: 'warning' as const }
      case 'shipped': return { label: 'Enviada', variant: 'success' as const }
      case 'delivered': return { label: 'Entregada', variant: 'success' as const }
      case 'cancelled': return { label: 'Cancelada', variant: 'destructive' as const }
    }
  })()
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>
}

function PaymentStatusBadge({ status }: { status: OrderDetail['order']['paymentStatus'] }) {
  const cfg = (() => {
    switch (status) {
      case 'paid': return { label: 'paid', color: 'text-emerald-400' }
      case 'pending': return { label: 'pending', color: 'text-yellow-400' }
      case 'failed': return { label: 'failed', color: 'text-red-400' }
      case 'refunded': return { label: 'refunded', color: 'text-neutral-400' }
    }
  })()
  return <span className={cn('text-sm font-mono', cfg.color)}>{cfg.label}</span>
}

function ProgressStepper({ status }: { status: OrderDetail['order']['status'] }) {
  const steps = [
    { key: 'pending', label: 'Pendiente', icon: Clock },
    { key: 'processing', label: 'En Proceso', icon: Box },
    { key: 'shipped', label: 'Enviado', icon: Truck },
    { key: 'delivered', label: 'Entregado', icon: CheckCircle2 },
  ] as const

  const order = ['pending', 'processing', 'shipped', 'delivered']
  const currentIdx = status === 'cancelled' ? -1 : order.indexOf(status)

  return (
    <div className="flex items-center justify-between gap-2">
      {steps.map((s, idx) => {
        const reached = idx <= currentIdx
        const Icon = s.icon
        return (
          <div key={s.key} className="flex items-center gap-3 flex-1">
            <div
              className={cn(
                'flex flex-col items-center gap-2 min-w-0',
                reached ? '' : 'opacity-40',
              )}
            >
              <div
                className={cn(
                  'size-10 rounded-full flex items-center justify-center border-2',
                  reached
                    ? 'bg-emerald-900/30 border-emerald-500 text-emerald-400'
                    : 'bg-neutral-900 border-neutral-700 text-neutral-500',
                )}
              >
                <Icon className="size-5" />
              </div>
              <p className="text-xs font-medium">{s.label}</p>
            </div>
            {idx < steps.length - 1 && (
              <ChevronRight className={cn('size-5 shrink-0', idx < currentIdx ? 'text-emerald-500' : 'text-neutral-700')} />
            )}
          </div>
        )
      })}
    </div>
  )
}

function Total({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-neutral-400">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  )
}

function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(amount / 100)
}
